import { Box, ClickAwayListener, Divider, FormControl, IconButton, Popper } from '@mui/material';
import { useRef, useState } from 'react';
import {
  NestedFieldsSelector,
  NestedFieldsField,
} from '../../common/fieldsSelectors/NestedFieldsSelector';
import FieldLabel from '../../common/FieldLabel';
import { Plus as PlusIcon } from '@sweep-io/sweep-design/dist/icons';
import { SmileyIcon } from '../../../icons/generalIcons';
import EmojiPicker, { EmojiClickData, EmojiStyle } from 'emoji-picker-react';
import { colors } from '@sweep-io/sweep-design';
import React from 'react';
import { FieldContext } from '../../../types/enums/FieldContext';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { AssignmentFieldMenu } from './AssignmentFieldMenu';
import { ASSIGNMENT_MESSAGE_OPTIONS, AssignmentFieldOption } from './assignmentFieldOptions';
import { useSweepFieldsInfo } from '../../../sweep-fields/useSweepFieldsInfo';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import { DynamicTagNode } from '../lexical-editor/DynamicTagNode';
import { DynamicTagPlugin, INSERT_DYNAMIC_TAG_COMMAND } from '../lexical-editor/DynamicTagPlugin';
import {
  messageStructToLexicalState,
  lexicalStateToMessageStruct,
} from '../lexical-editor/convertMessageStruct';
import { telemetry } from '../../../telemetry';

interface SlackNewMessageEditorLexicalProps {
  readonly?: boolean;
  messageStruct: SlackMessageStruct;
  crmOrgId: string;
  onChange: (messageStruct: SlackMessageStruct) => any;
  objectName: string;
  label: string;
  isRequired?: boolean;
  hideEmoji?: boolean;
  infoTooltipTitle?: string;
  customOnKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => any;
  placeholder?: string;
  isHideDynamicField?: boolean;
  showAssignmentSelector?: boolean;
}

const lexicalConfig = {
  namespace: 'SlackMessageEditor',
  onError: (error: Error) => {
    telemetry.captureError(error, { errorInfo: error.message });
  },
  nodes: [DynamicTagNode],
  theme: {},
};

function EditorContent({
  readonly,
  messageStruct,
  crmOrgId,
  onChange,
  objectName,
  label,
  isRequired,
  hideEmoji,
  infoTooltipTitle,
  placeholder,
  isHideDynamicField,
  showAssignmentSelector,
}: SlackNewMessageEditorLexicalProps) {
  const [editor] = useLexicalComposerContext();
  const { priorValueSlackMessages: priorValueSlackMessagesFt, slackAssignee: slackAssigneeFt } =
    useFeatureToggle();
  const { getLabelFromIdsWithObjectType } = useSweepFieldsInfo();

  const shouldShowAssignmentSelector = slackAssigneeFt && showAssignmentSelector;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isEmojiVisible, setEmojiVisible] = useState(false);
  const [localFieldContext, setLocalFieldContext] = useState<FieldContext>(
    FieldContext.CURRENT_VALUE,
  );
  const initializedRef = useRef(false);

  React.useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (!messageStruct?.template) return;

    const fieldLabelResolver = (fieldIds: string[]) => {
      const labels = getLabelFromIdsWithObjectType(fieldIds);
      return labels.filter(Boolean).join('.');
    };

    const serializedState = messageStructToLexicalState(messageStruct, fieldLabelResolver);

    try {
      const parsedState = editor.parseEditorState(JSON.stringify(serializedState));
      editor.setEditorState(parsedState);
    } catch (e) {
      telemetry.captureError(e, {
        errorInfo: 'Failed to load initial editor state from messageStruct',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    editor.setEditable(!readonly);
  }, [editor, readonly]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    return editor.registerRootListener((rootElement, prevRootElement) => {
      const handleBlur = () => {
        const newStruct = lexicalStateToMessageStruct(editor.getEditorState());
        onChangeRef.current(newStruct);
      };
      if (prevRootElement) {
        prevRootElement.removeEventListener('blur', handleBlur);
      }
      if (rootElement) {
        rootElement.addEventListener('blur', handleBlur);
      }
    });
  }, [editor]);

  return (
    <FormControl
      sx={{
        width: '100%',
        marginBottom: '20px',
        maxWidth: '1148px',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          marginBottom: '3px',
          '& div': {
            color: colors.grey[800],
            fontSize: '12px',
            fontWeight: '400',
          },
        }}
      >
        <FieldLabel
          label={label}
          markLabelAsRequired={isRequired}
          infoTooltipTitle={infoTooltipTitle}
          showTooltip={!!infoTooltipTitle}
        />
      </Box>
      <Box
        sx={{
          background: '#FFFFFF',
          border: `1px solid ${colors.grey[300]}`,
          '&:focus-within': {
            border: `1px solid ${colors.blue[500]}`,
          },
          borderRadius: '4px',
          fontWeight: '400',
          position: 'relative',
        }}
      >
        {/* Editor area */}
        <Box
          sx={{
            background: '#FFFFFF',
            borderRadius: '4px',
            fontWeight: '400',
            position: 'relative',
            '& .slack-message-lexical-editor': {
              outline: 'none',
              padding: '12px 15px',
              minHeight: '40px',
              fontFamily: 'Inter',
              fontSize: '14px',
              lineHeight: '1.42',
              '& p': {
                margin: 0,
              },
            },
          }}
        >
          <PlainTextPlugin
            contentEditable={<ContentEditable className="slack-message-lexical-editor" />}
            placeholder={
              <Box
                sx={{
                  position: 'absolute',
                  top: '9px',
                  left: '15px',
                  color: colors.grey[500],
                  fontSize: '14px',
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {placeholder ?? 'e.g. great job for closing the deal! 🌈✨🐠'}
              </Box>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <DynamicTagPlugin />
        </Box>

        {/* Toolbar area */}
        <Box
          sx={{
            background: '#FFFFFF',
            borderRadius: '0 0 4px 4px',
            height: '40px',
            display: 'flex',
            flexDirection: 'row-reverse',
            position: 'relative',
            alignItems: 'center',
            paddingRight: '12px',
            gap: 1,
          }}
        >
          {/* Assignment selector */}
          {shouldShowAssignmentSelector && (
            <Box>
              <AssignmentFieldMenu
                options={ASSIGNMENT_MESSAGE_OPTIONS}
                onSelect={(option: AssignmentFieldOption) => {
                  editor.focus();
                  setTimeout(() => {
                    editor.dispatchCommand(INSERT_DYNAMIC_TAG_COMMAND, {
                      tagType: 'assignment',
                      value: option.value,
                      label: option.label,
                    });
                  }, 100);
                }}
                disabled={readonly}
                buttonVariant="flat-grey"
              />
            </Box>
          )}

          {shouldShowAssignmentSelector && !isHideDynamicField && (
            <Divider orientation="vertical" variant="middle" flexItem sx={{ margin: '12px 0' }} />
          )}

          {/* Dynamic field selector */}
          {!isHideDynamicField && (
            <Box>
              <NestedFieldsSelector
                readonly={readonly}
                nestedPath={[]}
                customButtonStartIcon={<PlusIcon size="medium" color={colors.grey[800]} />}
                crmOrgId={crmOrgId}
                objectType={objectName}
                useCustomButton
                customButtonText={
                  shouldShowAssignmentSelector ? 'From Salesforce' : 'Insert Dynamic field'
                }
                customButtonSx={{
                  borderRadius: '50px',
                  height: '32px',
                  fontSize: '14px',
                  background: 'transparent',
                  color: colors.grey[800],
                  border: 'none',
                  textTransform: 'unset',
                  p: '4px 8px',
                  '& span': {
                    fontWeight: 400,
                    fontSize: '14px',
                  },
                  '&:hover': {
                    background: colors.white,
                    color: colors.grey[900],
                  },
                }}
                onChange={({ fieldIds, fieldContext, fieldLabels }: NestedFieldsField) => {
                  const isPriorValue = fieldContext === FieldContext.PRIOR_VALUE;
                  const fieldText = fieldLabels.join('.');
                  const fieldTitle = isPriorValue ? `Prior value of ${fieldText}` : fieldText;

                  editor.focus();
                  setTimeout(() => {
                    editor.dispatchCommand(INSERT_DYNAMIC_TAG_COMMAND, {
                      tagType: 'field',
                      fieldIds,
                      fieldContext: fieldContext ?? undefined,
                      label: fieldTitle,
                    });
                  }, 200);

                  setLocalFieldContext(FieldContext.CURRENT_VALUE);
                }}
                fieldContext={localFieldContext}
                displayFieldContextMenu={priorValueSlackMessagesFt}
              />
            </Box>
          )}

          {!(hideEmoji || isHideDynamicField) && (
            <Divider orientation="vertical" variant="middle" flexItem sx={{ margin: '12px 0' }} />
          )}

          {/* Emoji picker */}
          {!hideEmoji && (
            <IconButton
              disabled={readonly}
              aria-label="add emoji"
              onClick={(event) => {
                if (!anchorEl) {
                  setAnchorEl(event.currentTarget);
                }
                setEmojiVisible(!isEmojiVisible);
              }}
              className={'openPicker'}
            >
              <SmileyIcon className={'openPicker'} />
            </IconButton>
          )}
          <Popper
            sx={{ zIndex: 1600 }}
            open={isEmojiVisible}
            anchorEl={anchorEl}
            placement="left-end"
          >
            <ClickAwayListener
              onClickAway={() => {
                setEmojiVisible(false);
              }}
            >
              <Box
                sx={{
                  border: '1px solid #D9D9D9',
                  borderRadius: '10px',
                }}
              >
                <EmojiPicker
                  emojiStyle={EmojiStyle.NATIVE}
                  skinTonesDisabled
                  autoFocusSearch={false}
                  previewConfig={{ showPreview: false }}
                  style={{ '--epr-emoji-size': '26px' } as React.CSSProperties}
                  onEmojiClick={(emojiData: EmojiClickData) => {
                    editor.update(() => {
                      const selection = $getSelection();
                      if ($isRangeSelection(selection)) {
                        selection.insertText(emojiData.emoji);
                      }
                    });
                    setEmojiVisible(false);
                  }}
                />
              </Box>
            </ClickAwayListener>
          </Popper>
        </Box>
      </Box>
    </FormControl>
  );
}

export const SlackNewMessageEditorLexical = (props: SlackNewMessageEditorLexicalProps) => {
  return (
    <LexicalComposer initialConfig={lexicalConfig}>
      <EditorContent {...props} />
    </LexicalComposer>
  );
};
